/**
 Copyright Highway9 Networks Inc.
 */
import {
  PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  nanoid,
  type EntityState,
} from "@reduxjs/toolkit";
import { metricsTemplateService } from "~/services";
import {
  CategoryGraph,
  MetricGraph,
  defaultMetrics,
  defaultGraphs,
  defaultTemplate,
  defaultCategory,
  AggregationType,
} from "~/types/metricGraph";
import type { RootState } from "..";
import { HIGHWAY9 } from "~/constants";
import { createSocketPatchReducer } from "../events/socketPatch";

export type KPIFilterType = "all" | "poor-aggregates" | "anomalies" | "healthy";

export const kpiTemplatesAdapter = createEntityAdapter<MetricGraph>({
  selectId: (template) => template.id!,
});

interface KpiTemplateState {
  open: boolean;
  templatesData: EntityState<MetricGraph>;
  /** Working draft while editing; not selection identity — mutated until updateTemplate. */
  currentEditable: MetricGraph | null;
  templateLoading: boolean;
  kpiFilter: KPIFilterType;
  vendor: string;
  technology: string;
  testGraphOptions: {
    graph: Omit<CategoryGraph, "graphOptions">;
    sourceObj: string;
  };
}

const initialState: KpiTemplateState = {
  open: false,
  templatesData: kpiTemplatesAdapter.getInitialState(),
  currentEditable: null,
  templateLoading: false,
  technology: "4G",
  kpiFilter: "all",
  vendor: HIGHWAY9,
  testGraphOptions: {
    graph: defaultGraphs,
    sourceObj: "radio",
  },
};

export const fetchMetricsTemplate = createAsyncThunk(
  `kpiTemplate/fetchMetricsTemplate`,
  metricsTemplateService.getTemplates,
);

export const kpiTemplateSlice = createSlice({
  name: "kpiTemplate",
  initialState,
  reducers: {
    setOpen(state, action: PayloadAction<boolean>) {
      state.open = action.payload;
    },
    setTemplateLoading(state, action: PayloadAction<boolean>) {
      state.templateLoading = action.payload;
    },
    setCurrentEditableTemplate(state, action: PayloadAction<MetricGraph | null>) {
      // MetricGraph nests JSX/Highcharts types that Immer WritableDraft cannot express.
      state.currentEditable = action.payload as typeof state.currentEditable;
    },
    setTechnology(state, action: PayloadAction<string>) {
      state.technology = action.payload;
    },
    setKpiFilter(state, action: PayloadAction<KPIFilterType>) {
      state.kpiFilter = action.payload;
    },

    setVendor(state, action: PayloadAction<string>) {
      state.vendor = action.payload;
    },

    setTestGraphOptions(
      state,
      action: PayloadAction<{
        graph: CategoryGraph;
        sourceObj: string;
      }>,
    ) {
      state.testGraphOptions = {
        graph: action.payload.graph as Omit<CategoryGraph, "graphOptions">,
        sourceObj: action.payload.sourceObj,
      };
    },

    deleteTemplate(state, action: PayloadAction<string>) {
      kpiTemplatesAdapter.removeOne(state.templatesData as EntityState<MetricGraph>, action.payload);
    },

    addTemplate(state) {
      const count = state.templatesData.ids.length;
      kpiTemplatesAdapter.addOne(state.templatesData as EntityState<MetricGraph>, {
        ...(defaultTemplate as MetricGraph),
        vendorType: ToCamelCase(state.vendor),
        technology: state.technology,
        isDraft: true,
        isSystem: false,
        name: `New Template ${count + 1}`,
        id: `metricsTemplate-new-${nanoid(16)}`,
      });
    },

    cloneTemplate(state, action: PayloadAction<string>) {
      const template = state.templatesData.entities[action.payload];
      if (!template) return;

      const clone = JSON.parse(JSON.stringify(template)) as MetricGraph;
      clone.id = `metricsTemplate-clone-${nanoid(16)}`;
      clone.name = `${clone.name}_1`;
      clone.isDraft = true;
      clone.isSystem = false;
      kpiTemplatesAdapter.addOne(state.templatesData as EntityState<MetricGraph>, clone);
      state.currentEditable = clone as typeof state.currentEditable;
    },

    updateTemplate(state) {
      if (!state.currentEditable?.id) return;

      const template = JSON.parse(JSON.stringify(state.currentEditable)) as MetricGraph;
      const existing = state.templatesData.entities[template.id!];
      if (!existing) return;

      // if it is a new template and not a system template then set it as draft.
      template.isDraft = template.isSystem
        ? template.isDefault !== existing.isDefault
        : true;
      kpiTemplatesAdapter.upsertOne(state.templatesData as EntityState<MetricGraph>, template);
    },

    setTemplateName(state, action: PayloadAction<string>) {
      if (state.currentEditable) {
        state.currentEditable.name = action.payload;
      }
    },

    setTemplateDefault(state, action: PayloadAction<boolean>) {
      if (!state.currentEditable?.id) return;

      state.currentEditable.isDefault = action.payload;
      const templatesData = state.templatesData as EntityState<MetricGraph>;
      const templates = kpiTemplatesAdapter
        .getSelectors()
        .selectAll(templatesData)
        .map((d) => ({
          ...d,
          isDefault: d.id === state.currentEditable?.id ? action.payload : false,
        }));
      kpiTemplatesAdapter.setAll(templatesData, templates);
    },

    addNewGroup(state) {
      if (state.currentEditable) {
        state.currentEditable.categories.push({
          ...defaultCategory,
          graphs: [
            {
              ...(defaultGraphs as CategoryGraph),
              id: `chart-new-${nanoid(16)}`,
            },
          ],
          id: `category-new-${nanoid(16)}`,
        } as (typeof state.currentEditable.categories)[number]);
      }
    },

    removeTemplateGroup(state, action: PayloadAction<number>) {
      if (state.currentEditable) {
        state.currentEditable.categories.splice(action.payload, 1);
      }
    },

    reorderCategory(state, action: PayloadAction<MetricGraph["categories"]>) {
      if (state.currentEditable) {
        state.currentEditable.categories = action.payload as typeof state.currentEditable.categories;
      }
    },

    setGroupName(state, action: PayloadAction<{ name: string; index: number }>) {
      if (state.currentEditable) {
        const { name, index } = action.payload;
        state.currentEditable.categories[index].name = name;
      }
    },

    setGroupIcon(state, action: PayloadAction<{ icon: string; index: number }>) {
      if (state.currentEditable) {
        const { icon, index } = action.payload;
        state.currentEditable.categories[index].icon = icon;
      }
    },

    setGroupColumnCount(
      state,
      action: PayloadAction<{ column: 1 | 2 | 3; index: number }>,
    ) {
      if (state.currentEditable) {
        const { column, index } = action.payload;
        state.currentEditable.categories[index].columnCount = column;
      }
    },

    addNewChart(state, action: PayloadAction<number>) {
      if (state.currentEditable) {
        const index = action.payload;
        state.currentEditable.categories[index].graphs.push({
          ...(defaultGraphs as CategoryGraph),
          id: `chart-new-${nanoid(16)}`,
        } as (typeof state.currentEditable.categories)[number]["graphs"][number]);
      }
    },

    removeChart(
      state,
      action: PayloadAction<{ groupIndex: number; chartIndex: number }>,
    ) {
      if (state.currentEditable) {
        const { groupIndex, chartIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs.splice(chartIndex, 1);
      }
    },

    reorderGraphs(
      state,
      action: PayloadAction<{ newList: CategoryGraph[]; groupIndex: number }>,
    ) {
      const { newList, groupIndex } = action.payload;
      if (state.currentEditable) {
        state.currentEditable.categories[groupIndex].graphs =
          newList as (typeof state.currentEditable.categories)[number]["graphs"];
      }
    },

    setChartName(
      state,
      action: PayloadAction<{ name: string; groupIndex: number; chartIndex: number }>,
    ) {
      if (state.currentEditable) {
        const { name, groupIndex, chartIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].name = name;
      }
    },

    setChartType(
      state,
      action: PayloadAction<{ value: string; groupIndex: number; chartIndex: number }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].graphType = value;
      }
    },

    addNewMetric(
      state,
      action: PayloadAction<{ groupIndex: number; chartIndex: number }>,
    ) {
      if (state.currentEditable) {
        const { groupIndex, chartIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics.push({
          ...defaultMetrics,
          id: `metric-new-${nanoid(16)}`,
        });
      }
    },

    removeMetric(
      state,
      action: PayloadAction<{ groupIndex: number; chartIndex: number; index: number }>,
    ) {
      if (state.currentEditable) {
        const { groupIndex, chartIndex, index } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics.splice(
          index,
          1,
        );
      }
    },

    reorderMetrics(
      state,
      action: PayloadAction<{
        newList: MetricGraph["categories"][0]["graphs"][0]["metrics"];
        groupIndex: number;
        chartIndex: number;
      }>,
    ) {
      const { newList, groupIndex, chartIndex } = action.payload;
      if (state.currentEditable) {
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics = newList;
      }
    },

    setMetricName(
      state,
      action: PayloadAction<{
        value: string;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[
          metricIndex
        ].name = value;
      }
    },

    setMetricUnit(
      state,
      action: PayloadAction<{
        value: string;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[
          metricIndex
        ].unit = value;
      }
    },

    setMetricAggregationType(
      state,
      action: PayloadAction<{
        value: string | AggregationType;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[metricIndex].aggregationType = value;
      }
    },

    setMetricTarget1(
      state,
      action: PayloadAction<{
        value: number | undefined;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
        state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[metricIndex].target1 = value;
      }
    },

    setMetricTarget2(
      state,
      action: PayloadAction<{
        value: number | undefined;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
                state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[metricIndex].target2 = value;
      }
    },

    setMetricTargetOperator(
      state,
      action: PayloadAction<{
        value: string | undefined;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
                state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[metricIndex].targetOperator = value;
      }
    },

    setMetricFormula(
      state,
      action: PayloadAction<{
        value: string;
        groupIndex: number;
        chartIndex: number;
        metricIndex: number;
      }>,
    ) {
      if (state.currentEditable) {
        const { value, groupIndex, chartIndex, metricIndex } = action.payload;
                state.currentEditable.categories[groupIndex].graphs[chartIndex].metrics[metricIndex].key = value;
      }
    },
    applySocketPatches: createSocketPatchReducer(
      kpiTemplatesAdapter,
      (s) => s.templatesData as EntityState<MetricGraph>,
    ),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMetricsTemplate.fulfilled, (state, action) => {
        kpiTemplatesAdapter.setAll(
          state.templatesData as EntityState<MetricGraph>,
          action.payload as MetricGraph[],
        );
      })
      .addCase(fetchMetricsTemplate.rejected, (state, action) => {
        kpiTemplatesAdapter.removeAll(state.templatesData as EntityState<MetricGraph>);
        console.log(action.error);
      });
  },
});

export const kpiTemplateActions = kpiTemplateSlice.actions;
export default kpiTemplateSlice.reducer;

const kpiTemplateEntitySelectors = kpiTemplatesAdapter.getSelectors(
  (state: RootState) => state.kpiTemplate.templatesData,
);

export const kpiTemplateData = kpiTemplateEntitySelectors.selectAll;
export const kpiTemplateEntities = kpiTemplateEntitySelectors.selectEntities;

export const kpiTemplateDataById =
  (id: string) =>
    (state: RootState): MetricGraph | undefined =>
      id ? kpiTemplateEntities(state)[id] : undefined;

export const kpiTemplateOpen = (state: RootState) => state.kpiTemplate.open;
export const kpiTemplateTechnology = (state: RootState) => state.kpiTemplate.technology;
export const kpiTemplateLoading = (state: RootState) => state.kpiTemplate.templateLoading;

export const kpiTemplateTestGraphOptions = (state: RootState) =>
  state.kpiTemplate.testGraphOptions;
export const kpiTemplateCurrentEditable = (state: RootState) =>
  state.kpiTemplate.currentEditable;
export const kpiTemplateVendor = (state: RootState) => state.kpiTemplate.vendor;
export const kpiTemplateKpiFilter = (state: RootState) => state.kpiTemplate.kpiFilter;

function ToCamelCase(string?: string) {
  if (!string) return "";
  const str = string.toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}
